import {
  workforceSessionCurrentContractVersion,
  workforceSessionCurrentRoutePath,
  workforceSessionLoginContractVersion,
  workforceSessionLoginRoutePath,
  workforceSessionLogoutContractVersion,
  workforceSessionLogoutRoutePath,
  workforceSessionSubjectsContractVersion,
  workforceSessionSubjectsRoutePath,
  type WorkforceSessionCurrentResponseV1,
  type WorkforceSessionLoginRequestV1,
  type WorkforceSessionLoginResponseV1,
  type WorkforceSessionLogoutResponseV1,
  type WorkforceSessionSubjectSummaryV1,
  type WorkforceSessionSubjectsResponseV1,
  type WorkforceSessionSummaryV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";

export interface WorkforceSessionLoginCredentials {
  email: string;
  password: string;
}

function readBrowserOrigin(): string | null {
  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin;
  }

  return null;
}

function isLoopbackOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);

    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  } catch {
    return false;
  }
}

function resolveFetchBaseUrls(config: MobileRuntimeConfig): string[] {
  const browserOrigin = readBrowserOrigin();
  const prefersBrowserOrigin =
    typeof browserOrigin === "string" &&
    browserOrigin.length > 0 &&
    !isLoopbackOrigin(browserOrigin);
  const baseUrls = (
    prefersBrowserOrigin
      ? [browserOrigin, config.sessionApiBaseUrl]
      : [config.sessionApiBaseUrl, browserOrigin]
  ).filter((value): value is string => typeof value === "string" && value.length > 0);

  return [...new Set(baseUrls)];
}

function buildWorkforceSessionCurrentUrl(baseUrl: string): string {
  return new URL(workforceSessionCurrentRoutePath, baseUrl).toString();
}

function buildWorkforceSessionLoginUrl(baseUrl: string): string {
  return new URL(workforceSessionLoginRoutePath, baseUrl).toString();
}

function buildWorkforceSessionLogoutUrl(baseUrl: string): string {
  return new URL(workforceSessionLogoutRoutePath, baseUrl).toString();
}

function buildWorkforceSessionSubjectsUrl(
  baseUrl: string,
  tenantId: string,
): string {
  const url = new URL(workforceSessionSubjectsRoutePath, baseUrl);

  url.searchParams.set("tenantId", tenantId);

  return url.toString();
}

function buildWorkforceSessionLoginRequest(
  config: MobileRuntimeConfig,
  subjectKey: string | null,
  credentials?: WorkforceSessionLoginCredentials | null,
): WorkforceSessionLoginRequestV1 {
  return {
    schemaVersion: workforceSessionLoginContractVersion,
    tenantId: config.requestTenantId,
    ...(subjectKey
      ? {
          subjectKey,
        }
      : {}),
    ...(credentials
      ? {
          email: credentials.email,
          password: credentials.password,
        }
      : {}),
  };
}

function readWorkforceSessionSubjectKey(
  session: WorkforceSessionSummaryV1,
): string | null {
  return typeof session.subjectKey === "string" && session.subjectKey.length > 0
    ? session.subjectKey
    : null;
}

function matchesExpectedSubject(
  expectedSubjectKey: string | null,
  session: WorkforceSessionSummaryV1,
): boolean {
  return expectedSubjectKey == null
    ? true
    : readWorkforceSessionSubjectKey(session) === expectedSubjectKey;
}

export function matchesWorkforceSessionConfig(
  config: MobileRuntimeConfig,
  session: WorkforceSessionSummaryV1,
  expectedSubjectKey: string | null = config.workforceSessionSubjectKey,
): boolean {
  return (
    session.tenantId === config.requestTenantId &&
    matchesExpectedSubject(expectedSubjectKey, session)
  );
}

export async function readCurrentWorkforceSession(
  config: MobileRuntimeConfig,
): Promise<WorkforceSessionSummaryV1 | null> {
  const baseUrls = resolveFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("clinician-workbench-session-base-url-unavailable");
  }

  let sawUnauthorized = false;
  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(buildWorkforceSessionCurrentUrl(baseUrl), {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
        method: "GET",
      });

      if (response.status === 401) {
        sawUnauthorized = true;
        continue;
      }

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-workforce-session-current-failed-${response.status}`,
        );
      }

      const payload =
        (await response.json()) as WorkforceSessionCurrentResponseV1;

      if (
        payload.schemaVersion !== workforceSessionCurrentContractVersion ||
        payload.sessionMode !== "browser-session" ||
        readWorkforceSessionSubjectKey(payload.session) == null
      ) {
        throw new Error(
          "clinician-workbench-workforce-session-current-schema-mismatch",
        );
      }

      return payload.session;
    } catch (error) {
      lastError = error;
    }
  }

  if (sawUnauthorized) {
    return null;
  }

  throw lastError ?? new Error("clinician-workbench-workforce-session-current-failed");
}

export async function listWorkforceSessionSubjects(
  config: MobileRuntimeConfig,
): Promise<WorkforceSessionSubjectSummaryV1[]> {
  const baseUrls = resolveFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("clinician-workbench-session-base-url-unavailable");
  }

  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(
        buildWorkforceSessionSubjectsUrl(baseUrl, config.requestTenantId),
        {
          credentials: "include",
          headers: {
            accept: "application/json",
          },
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-workforce-session-subjects-failed-${response.status}`,
        );
      }

      const payload =
        (await response.json()) as WorkforceSessionSubjectsResponseV1;

      if (
        payload.schemaVersion !== workforceSessionSubjectsContractVersion ||
        payload.tenantId !== config.requestTenantId
      ) {
        throw new Error(
          "clinician-workbench-workforce-session-subjects-schema-mismatch",
        );
      }

      return payload.subjects;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("clinician-workbench-workforce-session-subjects-failed");
}

export function resolveAutoSelectedWorkforceSessionSubjectKey(
  subjects: readonly WorkforceSessionSubjectSummaryV1[],
): string | null {
  const explicitSubject = subjects.find((subject) => subject.isAutoSelected);

  if (explicitSubject) {
    return explicitSubject.subjectKey;
  }

  return subjects.length === 1 ? subjects[0]?.subjectKey ?? null : null;
}

export async function loginWorkforceSession(
  config: MobileRuntimeConfig,
  subjectKey: string | null = config.workforceSessionSubjectKey,
  credentials?: WorkforceSessionLoginCredentials | null,
): Promise<WorkforceSessionSummaryV1> {
  const baseUrls = resolveFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("clinician-workbench-session-base-url-unavailable");
  }

  const requestBody = JSON.stringify(
    buildWorkforceSessionLoginRequest(config, subjectKey, credentials),
  );
  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(buildWorkforceSessionLoginUrl(baseUrl), {
        body: requestBody,
        credentials: "include",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-workforce-session-login-failed-${response.status}`,
        );
      }

      const payload = (await response.json()) as WorkforceSessionLoginResponseV1;

      if (
        payload.schemaVersion !== workforceSessionLoginContractVersion ||
        payload.sessionMode !== "browser-session" ||
        readWorkforceSessionSubjectKey(payload.session) == null ||
        !matchesWorkforceSessionConfig(config, payload.session, subjectKey)
      ) {
        throw new Error(
          "clinician-workbench-workforce-session-login-schema-mismatch",
        );
      }

      return payload.session;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("clinician-workbench-workforce-session-login-failed");
}

export async function logoutWorkforceSession(
  config: MobileRuntimeConfig,
): Promise<void> {
  const baseUrls = resolveFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("clinician-workbench-session-base-url-unavailable");
  }

  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(buildWorkforceSessionLogoutUrl(baseUrl), {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(
          `clinician-workbench-workforce-session-logout-failed-${response.status}`,
        );
      }

      const payload = (await response.json()) as WorkforceSessionLogoutResponseV1;

      if (
        payload.schemaVersion !== workforceSessionLogoutContractVersion ||
        payload.sessionMode !== "browser-session" ||
        payload.signedOut !== true
      ) {
        throw new Error(
          "clinician-workbench-workforce-session-logout-schema-mismatch",
        );
      }

      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("clinician-workbench-workforce-session-logout-failed");
}
